import { useEffect, useMemo, useState } from "react";
import api from "@/api/client";
import Header from "@/components/Header";
import BrandFooter from "@/components/BrandFooter";
import CardDetailPanel from "@/components/board/CardDetailPanel";
import { stageColor } from "@/lib/palette";
import { ChevronLeft, ChevronRight, Calendar as CalIcon, Clock, ListChecks, CalendarClock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function ymd(d) {
  return d.toISOString().slice(0, 10);
}

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

function buildGrid(monthDate) {
  // Build 6-week grid starting on Sunday for the month containing monthDate
  const first = startOfMonth(monthDate);
  const startWeekday = first.getDay(); // 0..6
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - startWeekday);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    days.push(d);
  }
  return days;
}

const MONTH_LABEL = (d) =>
  d.toLocaleString(undefined, { month: "long", year: "numeric" });

export default function CalendarPage() {
  const { user } = useAuth();
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [lists, setLists] = useState({});
  const [ownerFilter, setOwnerFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const grid = useMemo(() => buildGrid(cursor), [cursor]);
  const from = ymd(grid[0]);
  const to = ymd(grid[grid.length - 1]);

  useEffect(() => {
    Promise.all([
      api.get("/calendar/events", { params: { from_date: from, to_date: to, owner_id: ownerFilter || undefined } }),
      api.get("/users"),
      api.get("/templates"),
      api.get("/lists"),
    ]).then(([e, u, t, l]) => {
      setEvents(e.data);
      setUsers(u.data);
      setTemplates(t.data);
      setLists(l.data);
    });
  }, [from, to, ownerFilter]);

  const tplByStage = useMemo(
    () => Object.fromEntries(templates.map((t) => [t.stage_number, t])),
    [templates]
  );

  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      (map[e.date] = map[e.date] || []).push(e);
    });
    return map;
  }, [events]);

  const openCard = async (card_id) => {
    const { data } = await api.get(`/cards/${card_id}`);
    setSelected(data);
  };

  const monthIdx = cursor.getMonth();
  const todayKey = ymd(today);

  return (
    <div className="min-h-screen flex flex-col paper-bg">
      <Header />
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-7xl w-full mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
          <div>
            <div className="flex items-center gap-3">
              <CalIcon className="h-6 w-6 text-[#FBBF24]" />
              <h1 className="text-3xl font-extrabold tracking-tight text-[#15233B]">Calendar</h1>
            </div>
            <p className="text-sm text-[#15233B]/60 mt-1">
              Next actions and task due dates across the pipeline.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
              data-testid="calendar-prev-month"
              className="p-2 rounded-md border border-[#15233B]/15 hover:bg-[#15233B]/5"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))}
              data-testid="calendar-today"
              className="px-3 py-1.5 rounded-md border border-[#15233B]/15 hover:bg-[#15233B]/5 text-xs font-bold"
            >
              Today
            </button>
            <button
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
              data-testid="calendar-next-month"
              className="p-2 rounded-md border border-[#15233B]/15 hover:bg-[#15233B]/5"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <span className="ml-2 text-base font-extrabold text-[#15233B] tracking-tight" data-testid="calendar-month-label">
              {MONTH_LABEL(cursor)}
            </span>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-[#15233B]/10 rounded-md p-3 flex flex-wrap items-center gap-2 mb-4">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#15233B]/60">
            Owner
          </span>
          <select
            data-testid="calendar-owner-filter"
            value={ownerFilter}
            onChange={(e) => setOwnerFilter(e.target.value)}
            className="border border-[#15233B]/15 rounded px-2 py-1.5 text-xs bg-white font-semibold"
          >
            <option value="">All owners</option>
            <option value={user.id}>Just me</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
          <div className="ml-auto flex items-center gap-3 text-[11px] text-[#15233B]/70">
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3 text-[#FBBF24]" /> Next action
            </span>
            <span className="inline-flex items-center gap-1">
              <ListChecks className="h-3 w-3 text-[#0D9488]" /> Task due
            </span>
            <span className="inline-flex items-center gap-1">
              <CalendarClock className="h-3 w-3 text-[#7C3AED]" /> Interview
            </span>
          </div>
        </div>

        {/* Grid */}
        <div className="bg-white border border-[#15233B]/10 rounded-md overflow-hidden shadow-sm">
          <div className="grid grid-cols-7 bg-[#F6F3EC] text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d} className="px-3 py-2 border-b border-[#15233B]/10">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 grid-rows-6">
            {grid.map((d, i) => {
              const key = ymd(d);
              const inMonth = d.getMonth() === monthIdx;
              const isToday = key === todayKey;
              const dayEvents = eventsByDate[key] || [];
              return (
                <div
                  key={i}
                  data-testid={`cal-day-${key}`}
                  className={`min-h-[110px] border-b border-r border-[#15233B]/10 p-1.5 text-xs ${
                    inMonth ? "bg-white" : "bg-[#F6F3EC]/40"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-[11px] font-bold ${
                        isToday
                          ? "bg-[#FBBF24] text-white rounded-full px-2 py-0.5"
                          : inMonth
                            ? "text-[#15233B]"
                            : "text-[#15233B]/30"
                      }`}
                    >
                      {d.getDate()}
                    </span>
                    {dayEvents.length > 0 && (
                      <span className="text-[9px] font-bold text-[#15233B]/40">
                        {dayEvents.length}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 space-y-1 max-h-[78px] overflow-y-auto pr-0.5">
                    {dayEvents.slice(0, 4).map((e, ei) => {
                      const sc = stageColor(e.card_stage);
                      return (
                        <button
                          key={ei}
                          onClick={() => openCard(e.card_id)}
                          data-testid={`cal-event-${e.card_id}-${ei}`}
                          className="w-full text-left flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-[#F6F3EC] border border-[#15233B]/10"
                          title={`${e.card_full_name} · ${e.title}`}
                        >
                          <span
                            className="h-1.5 w-1.5 rounded-full shrink-0"
                            style={{ backgroundColor: sc.primary }}
                          />
                          {e.type === "interview" ? (
                            <CalendarClock className="h-2.5 w-2.5 text-[#7C3AED] shrink-0" />
                          ) : e.type === "next_action" ? (
                            <Clock className="h-2.5 w-2.5 text-[#FBBF24] shrink-0" />
                          ) : (
                            <ListChecks className="h-2.5 w-2.5 text-[#0D9488] shrink-0" />
                          )}
                          <span className="font-semibold text-[#15233B] truncate">
                            {e.card_full_name}
                          </span>
                        </button>
                      );
                    })}
                    {dayEvents.length > 4 && (
                      <div className="text-[10px] italic text-[#15233B]/50 pl-1">
                        +{dayEvents.length - 4} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {selected && (
        <CardDetailPanel
          card={selected}
          template={tplByStage[selected.current_stage]}
          templates={templates}
          users={users}
          lists={lists}
          onClose={() => setSelected(null)}
          onChanged={(u) => setSelected(u)}
        />
      )}
      <BrandFooter />
    </div>
  );
}
